import React from 'react'
import Select, { components } from 'react-select'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon } from '@hugeicons/core-free-icons'

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `rotate(${props.selectProps.menuIsOpen ? 180 : 0}deg)`,
        transition: 'transform 0.15s ease',
        color: props.isDisabled ? '#9CA3AF' : '#64748B',
        cursor: props.isDisabled ? 'not-allowed' : 'pointer'
      }}
    >
      <HugeiconsIcon icon={ArrowDown01Icon} size={16} />
    </span>
  </components.DropdownIndicator>
)

const GlobalSelect = ({
  value,
  onChange,
  options,
  placeholder,
  className,
  label = '',
  defaultValue = '',
  isDisabled = false,
  isSearchable = true,
  isClearable = false,
  noOptionsMessage = () => 'No options found',
  menuPlacement = 'auto',
  styles: customStyles = {},
  ...props
}) => {
  return (
    <div className='select'>
      {label && <label className='select__label'>{label}</label>}
      <Select
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        className={className}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        isClearable={isClearable}
        noOptionsMessage={noOptionsMessage}
        isOptionDisabled={option => option.isDisabled}
        menuPlacement={menuPlacement}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator
        }}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: '42px',
            height: '42px',
            borderRadius: '8px',
            borderColor: state.isDisabled
              ? '#E5E7EB'
              : (state.isFocused ? '#2F7D8A' : '#D1D5DB'),
            backgroundColor: state.isDisabled ? '#F3F4F6' : '#FFFFFF',
            boxShadow: state.isFocused ? '0 0 0 1px #2F7D8A' : 'none',
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            opacity: state.isDisabled ? 0.7 : 1,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontSize: '14px',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              borderColor: state.isDisabled
                ? '#E5E7EB'
                : (state.isFocused ? '#2F7D8A' : '#9CA3AF')
            },
            ...(customStyles.control ? customStyles.control(base, state) : {})
          }),
          valueContainer: (base, state) => ({
            ...base,
            padding: '0 14px',
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            ...(customStyles.valueContainer ? customStyles.valueContainer(base, state) : {})
          }),
          input: (base, state) => ({
            ...base,
            margin: 0,
            padding: 0,
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            background: 'transparent',
            color: '#1E293B',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            '& input': {
              border: 'none !important',
              outline: 'none !important',
              boxShadow: 'none !important',
              background: 'transparent !important'
            },
            ...(customStyles.input ? customStyles.input(base, state) : {})
          }),
          singleValue: (base, state) => ({
            ...base,
            fontSize: '14px',
            fontWeight: 500,
            color: state.isDisabled ? '#9CA3AF' : '#1E293B',
            margin: 0,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            ...(customStyles.singleValue ? customStyles.singleValue(base, state) : {})
          }),
          placeholder: (base, state) => ({
            ...base,
            fontSize: '14px',
            color: '#94A3B8',
            margin: 0,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            ...(customStyles.placeholder ? customStyles.placeholder(base, state) : {})
          }),
          dropdownIndicator: (base, state) => ({
            ...base,
            padding: '0 12px',
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            ...(customStyles.dropdownIndicator ? customStyles.dropdownIndicator(base, state) : {})
          }),
          menu: (base, state) => ({
            ...base,
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 8px 24px rgba(16, 21, 37, 0.12), 0 2px 6px rgba(16, 21, 37, 0.04)',
            padding: '4px',
            backgroundColor: '#FFFFFF',
            zIndex: 1000,
            marginTop: '4px',
            overflow: 'hidden',
            ...(customStyles.menu ? customStyles.menu(base, state) : {})
          }),
          menuList: (base, state) => ({
            ...base,
            padding: 0,
            maxHeight: '220px',
            ...(customStyles.menuList ? customStyles.menuList(base, state) : {})
          }),
          option: (base, state) => ({
            ...base,
            borderRadius: '6px',
            padding: '10px 14px',
            fontSize: '13.5px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontWeight: state.isSelected ? 500 : 400,
            color: state.isDisabled
              ? '#9CA3AF'
              : (state.isSelected ? '#2F7D8A' : (state.isFocused ? '#1E293B' : '#334155')),
            backgroundColor: state.isDisabled
              ? 'transparent'
              : (state.isSelected ? '#EAF8FA' : (state.isFocused ? '#F8FAFC' : 'transparent')),
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            opacity: state.isDisabled ? 0.6 : 1,
            transition: 'background-color 0.12s ease, color 0.12s ease',
            '&:active': {
              backgroundColor: state.isDisabled ? 'transparent' : '#EAF8FA'
            },
            ...(customStyles.option ? customStyles.option(base, state) : {})
          }),
          noOptionsMessage: (base, state) => ({
            ...base,
            fontSize: '13.5px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            color: '#94A3B8',
            padding: '10px 14px',
            textAlign: 'center',
            ...(customStyles.noOptionsMessage ? customStyles.noOptionsMessage(base, state) : {})
          })
        }}
        {...props}
      />
    </div>
  )
}

export default GlobalSelect
